import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { Link } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import { bannerComics, latestComics, popularComics, topRatedComics, comicCategories } from '@/mocks/comics';

export default function Home() {
  const [currentBanner, setCurrentBanner] = useState(0);
  const banners = bannerComics;

  const nextBanner = useCallback(() => {
    setCurrentBanner((prev) => (prev + 1) % banners.length);
  }, [banners.length]);

  useEffect(() => {
    const timer = setInterval(nextBanner, 5000);
    return () => clearInterval(timer);
  }, [nextBanner]);

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '51漫画 - 免费漫画全集在线观看',
    description: '51漫画在线观看页面，提供免费漫画全集在线阅读、下拉式阅读、实时阅读，无需下载即看热门漫画大全。',
    url: 'https://51manhua.com/',
    inLanguage: 'zh-CN',
    isPartOf: { '@type': 'WebSite', name: '51漫画', url: 'https://51manhua.com/' },
  };

  return (
    <>
      <SEOHead
        title="首页"
        description="51漫画在线观看页面，提供免费漫画全集在线阅读、下拉式阅读、实时阅读，无需下载即看热门漫画大全，支持网页观看与在线漫画。海量热门漫画、日漫、国漫、韩漫免费看。"
        keywords="51漫画在线观看,51漫画在线,51漫画在线看,在线阅读,免费漫画,下拉式漫画,漫画大全,实时阅读,网页观看,在线漫画"
        canonicalUrl="https://51manhua.com/"
        schemaObjects={[webPageSchema]}
      />

      <Navbar />

      <main>
        {/* Hero Banner */}
        <section className="relative w-full h-[500px] md:h-[600px] overflow-hidden">
          {banners.map((comic, index) => (
            <div
              key={comic.id}
              className={`absolute inset-0 transition-opacity duration-700 ${
                index === currentBanner ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img
                src={comic.cover}
                alt={comic.title}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/60"></div>
            </div>
          ))}

          <div className="absolute inset-0 flex items-center">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-6xl mx-auto">
                <div className="max-w-xl">
                  <span className="inline-block px-3 py-1 bg-primary-500 text-background-50 text-xs font-medium rounded-full mb-4 whitespace-nowrap">
                    {banners[currentBanner].status}
                  </span>
                  <h1 className="text-4xl md:text-5xl font-heading font-bold text-background-50 mb-3">
                    {banners[currentBanner].title}
                  </h1>
                  <p className="text-background-50/80 text-sm md:text-base leading-relaxed mb-6 line-clamp-3">
                    {banners[currentBanner].description}
                  </p>
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                    <Link
                      to={`/comic/${banners[currentBanner].id}`}
                      className="px-6 py-3 bg-primary-500 text-background-50 font-medium rounded-full text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      开始阅读 <i className="ri-arrow-right-line ml-1"></i>
                    </Link>
                    <div className="flex items-center gap-2 text-background-50/70 text-xs">
                      <span><i className="ri-star-fill text-accent-500 mr-1"></i>{banners[currentBanner].rating}</span>
                      <span className="w-1 h-1 rounded-full bg-background-50/40"></span>
                      <span><i className="ri-eye-line mr-1"></i>{banners[currentBanner].views}</span>
                      <span className="w-1 h-1 rounded-full bg-background-50/40"></span>
                      <span>共{banners[currentBanner].chapters}话</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
            {banners.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentBanner(index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 cursor-pointer ${
                  index === currentBanner ? 'bg-primary-500 w-6' : 'bg-background-50/50'
                }`}
                aria-label={`第${index + 1}张`}
              />
            ))}
          </div>
        </section>

        {/* Category Navigation */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="px-4 md:px-6">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">漫画分类</h2>
                <Link to="/category/all" className="text-sm text-primary-500 hover:text-primary-600 transition-colors flex items-center gap-1 whitespace-nowrap">
                  全部分类 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-7 lg:grid-cols-9 gap-3">
                {comicCategories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="flex flex-col items-center gap-2 p-4 rounded-xl bg-background-100 hover:bg-primary-50 transition-colors cursor-pointer group"
                  >
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors">
                      <i className={`${cat.icon} text-lg`}></i>
                    </div>
                    <span className="text-xs font-medium text-foreground-700 group-hover:text-primary-600 transition-colors whitespace-nowrap">{cat.name}</span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Latest Updates */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="px-4 md:px-6">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">最新更新</h2>
                <Link to="/category/all" className="text-sm text-primary-500 hover:text-primary-600 transition-colors flex items-center gap-1 whitespace-nowrap">
                  查看更多 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4" data-product-shop="">
                {latestComics.slice(0, 12).map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Popular Comics */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="px-4 md:px-6">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">人气排行</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {popularComics.slice(0, 8).map((comic, index) => (
                  <Link
                    key={comic.id}
                    to={`/comic/${comic.id}`}
                    className="flex items-center gap-4 p-3 rounded-xl bg-background-100 hover:bg-primary-50 transition-colors cursor-pointer group"
                  >
                    <div className={`w-8 h-8 flex items-center justify-center rounded-full font-heading font-bold text-sm flex-shrink-0 ${
                      index < 3 ? 'bg-primary-500 text-background-50' : 'bg-secondary-200 text-secondary-700'
                    }`}>
                      {index + 1}
                    </div>
                    <img src={comic.cover} alt={comic.title} className="w-14 h-14 md:w-16 md:h-16 rounded-lg object-cover flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors truncate">{comic.title}</h3>
                      <p className="text-xs text-foreground-500 mt-1 truncate">{comic.author}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-sm font-semibold text-accent-500"><i className="ri-star-fill text-xs mr-1"></i>{comic.rating}</div>
                      <div className="text-xs text-foreground-500 mt-1">{comic.views}</div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Top Rated */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="px-4 md:px-6">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">高分推荐</h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4" data-product-shop="">
                {topRatedComics.slice(0, 6).map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="px-4 md:px-6">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-10">为什么选择51漫画</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  { icon: 'ri-book-open-line', title: '海量漫画', desc: '收录数万部热门漫画，日漫、国漫、韩漫应有尽有，持续更新中' },
                  { icon: 'ri-smartphone-line', title: '多端适配', desc: '完美适配电脑、平板、手机等多种设备，随时随地畅享阅读' },
                  { icon: 'ri-download-2-line', title: '无需下载', desc: '纯网页在线观看，无需下载任何APP或插件，打开浏览器即可阅读' },
                  { icon: 'ri-speed-up-line', title: '极速加载', desc: '采用先进缓存技术，漫画加载速度快，下拉式阅读流畅无卡顿' },
                ].map((feature, index) => (
                  <div key={index} className="text-center p-6 rounded-xl bg-background-100">
                    <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mx-auto mb-4">
                      <i className={`${feature.icon} text-2xl`}></i>
                    </div>
                    <h3 className="text-sm font-heading font-bold text-foreground-900 mb-2">{feature.title}</h3>
                    <p className="text-xs text-foreground-600 leading-relaxed">{feature.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <ScrollToTop />
    </>
  );
}

function ComicCard({ comic }: { comic: typeof latestComics[0] }) {
  return (
    <Link to={`/comic/${comic.id}`} className="group cursor-pointer">
      <div className="relative aspect-[3/4] rounded-xl overflow-hidden mb-3">
        <img
          src={comic.cover}
          alt={comic.title}
          title={`${comic.title} - 51漫画在线观看`}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
        <div className="absolute top-2 left-2">
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
            comic.status === '连载中' ? 'bg-primary-500 text-background-50' : 'bg-secondary-500 text-background-50'
          }`}>
            {comic.status}
          </span>
        </div>
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3 pt-8">
          <div className="flex items-center gap-2 text-background-50/80 text-xs">
            <span><i className="ri-star-fill text-accent-500 text-xs mr-0.5"></i>{comic.rating}</span>
            <span>{comic.views}</span>
          </div>
        </div>
      </div>
      <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors truncate">{comic.title}</h3>
      <p className="text-xs text-foreground-500 mt-1 truncate">{comic.author} · 共{comic.chapters}话</p>
    </Link>
  );
}